import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface IframeSkeletonProps {
	className?: string
	label?: string | false
}

// Below these the toolbar and metadata push the player itself out of the panel.
const MIN_HEIGHT_FOR_TOOLBAR = 220
const MIN_HEIGHT_FOR_META = 380
const SIDEBAR_VIDEO_HEIGHT = 78
// Toolbar + sidebar heading sitting above the related-video list.
const SIDEBAR_HEADING_HEIGHT = 80

export function IframeSkeleton({ className, label = 'Loading' }: IframeSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()
	const withToolbar = size.height === 0 || size.height >= MIN_HEIGHT_FOR_TOOLBAR
	const withMeta = size.height === 0 || size.height >= MIN_HEIGHT_FOR_META
	// Stable keys for related videos in sidebar
	const videoKeys = useStableKeys(fitCount(size.height - SIDEBAR_HEADING_HEIGHT, SIDEBAR_VIDEO_HEIGHT, 1, 6))

	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn(
				'flex size-full flex-col border border-border/40 rounded-lg overflow-hidden bg-background/50 select-none shadow-sm',
				className,
			)}>
			{/* 1. Faux browser toolbar */}
			<div
				className={cn(
					'h-10 shrink-0 items-center justify-between border-b border-border/30 bg-muted/20 px-3',
					withToolbar ? 'flex' : 'hidden',
				)}>
				{/* macOS window controls style */}
				<div className='flex items-center gap-1.5 mr-4'>
					<div className='size-2.5 rounded-full bg-accent/30 animate-pulse' />
					<div className='size-2.5 rounded-full bg-accent/30 animate-pulse delay-75' />
					<div className='size-2.5 rounded-full bg-accent/30 animate-pulse delay-150' />
				</div>

				{/* Faux Navigation Controls */}
				<div className='hidden sm:flex items-center gap-2 mr-4 text-muted-foreground/30'>
					<div className='size-5 flex items-center justify-center rounded hover:bg-accent/40 cursor-default'>
						<div className='size-1.5 border-t border-l border-current -rotate-45 transform translate-x-[1px]' />
					</div>
					<div className='size-5 flex items-center justify-center rounded hover:bg-accent/40 cursor-default'>
						<div className='size-1.5 border-t border-r border-current rotate-45 transform -translate-x-[1px]' />
					</div>
					<div className='size-5 flex items-center justify-center rounded hover:bg-accent/40 cursor-default'>
						<div
							className='size-3 rounded-full border border-current border-t-transparent animate-spin'
							style={{ animationDuration: '3s' }}
						/>
					</div>
				</div>

				{/* Address Bar */}
				<div className='flex-1 max-w-md h-control rounded-md border border-border/20 bg-background/80 px-2.5 flex items-center gap-2'>
					<Skeleton className='h-3 w-3 shrink-0 rounded bg-accent/40' />
					<Skeleton className='h-2.5 w-32 rounded bg-accent/30' />
				</div>

				{/* Right Control Button */}
				<div className='flex items-center ml-4'>
					<Skeleton className='size-5 rounded bg-accent/30' />
				</div>
			</div>

			{/* 2. Embedded Video Content Area (Simulated YouTube Video Player) */}
			<div className='flex flex-1 min-h-0 bg-background/30 p-4 gap-4 overflow-hidden'>
				{/* Left Column: Video & Metadata */}
				<div className='flex-1 min-w-0 flex flex-col gap-3 overflow-hidden'>
					{/* Video Player Box (Neutral colors, no pitch blacks) */}
					<div
						className={cn(
							'relative w-full aspect-video rounded-xl bg-accent/5 border border-border/20 flex flex-col justify-between overflow-hidden min-h-0',
							// On short panels the player takes the whole body instead of overflowing at its 16:9 height.
							withMeta ? 'shrink-0' : 'flex-1',
						)}>
						{/* Play overlay */}
						<div className='absolute inset-0 flex items-center justify-center'>
							<div className='size-14 rounded-full flex items-center justify-center bg-accent/15 border border-accent/25 backdrop-blur-sm shadow-sm animate-pulse'>
								<div className='w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-accent/45 border-b-[8px] border-b-transparent ml-1' />
							</div>
						</div>

						{/* Top details */}
						<div className='flex items-center justify-between p-3.5 bg-accent/5 w-full'>
							<Skeleton className='h-4 w-40 rounded bg-accent/20' />
							<Skeleton className='h-6 w-12 rounded-full bg-accent/20' />
						</div>

						{/* Video controls bottom bar */}
						<div className='flex flex-col gap-2 p-3 bg-accent/5 w-full shrink-0'>
							{/* Progress Bar (Neutral gray tones, no thick red) */}
							<div className='flex items-center gap-2 w-full'>
								<div className='h-1 flex-1 bg-accent/15 rounded-full relative overflow-hidden'>
									<div className='absolute left-0 top-0 bottom-0 w-[45%] bg-accent/35 rounded-full' />
								</div>
								<Skeleton className='h-2.5 w-16 rounded bg-accent/20' />
							</div>

							{/* Buttons */}
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-3.5'>
									<div className='w-0 h-0 border-t-[5px] border-t-transparent border-l-[9px] border-l-accent/40 border-b-[5px] border-b-transparent' />
									<div className='flex items-center gap-1.5'>
										<Skeleton className='h-3.5 w-4 rounded bg-accent/30' />
										<Skeleton className='h-1.5 w-10 rounded bg-accent/20' />
									</div>
								</div>
								<div className='flex items-center gap-3'>
									<Skeleton className='h-3.5 w-4 rounded bg-accent/30' />
									<Skeleton className='h-3.5 w-4 rounded bg-accent/30' />
									<div className='size-3.5 border border-accent/30 rounded-sm' />
								</div>
							</div>
						</div>
					</div>

					{/* Title and stats */}
					<div className={cn('space-y-2.5 shrink-0', !withMeta && 'hidden')}>
						<Skeleton className='h-5 w-11/12 rounded bg-accent/50' />
						<div className='flex items-center justify-between gap-4 flex-wrap'>
							{/* Channel block */}
							<div className='flex items-center gap-2.5'>
								<Skeleton className='size-9 rounded-full bg-accent/40' />
								<div className='space-y-1.5'>
									<Skeleton className='h-3.5 w-24 rounded bg-accent/45' />
									<Skeleton className='h-2.5 w-16 rounded bg-accent/25' />
								</div>
								<Skeleton className='h-control w-20 rounded-full bg-accent/15 shrink-0 ml-1' />
							</div>

							<div className='flex items-center gap-2'>
								<Skeleton className='h-control w-24 rounded-full bg-accent/30' />
								<Skeleton className='h-control w-16 rounded-full bg-accent/25' />
								<Skeleton className='h-control w-20 rounded-full bg-accent/20' />
							</div>
						</div>
					</div>

					{/* Description box */}
					<div
						className={cn(
							'border border-border/20 rounded-xl p-3 bg-muted/25 flex-1 min-h-15 flex-col gap-2',
							withMeta ? 'flex' : 'hidden',
						)}>
						<Skeleton className='h-3 w-full rounded bg-accent/20' />
						<Skeleton className='h-3 w-5/6 rounded bg-accent/20' />
						<Skeleton className='h-3 w-2/3 rounded bg-accent/15' />
					</div>
				</div>

				{/* Right Column: Up Next List (Visible on Large Panels) */}
				<div className='w-72 shrink-0 hidden lg:flex flex-col gap-3.5 overflow-hidden border-l border-border/20 pl-4'>
					<Skeleton className='h-4 w-20 rounded bg-accent/40 mb-1 shrink-0' />
					{videoKeys.map(key => (
						<div key={key} className='flex gap-2 shrink-0'>
							{/* Thumbnail */}
							<div className='relative h-16 w-28 shrink-0 rounded bg-accent/20 overflow-hidden'>
								{/* Time Duration Badge Skeleton (Pure gray box, no raw text) */}
								<div className='absolute bottom-1 right-1 px-1 py-0.5 rounded bg-accent/30 scale-90 origin-bottom-right'>
									<Skeleton className='h-2 w-6 bg-accent/50' />
								</div>
							</div>
							{/* Title / stats */}
							<div className='flex-1 min-w-0 flex flex-col gap-1.5'>
								<Skeleton className='h-3.5 w-full rounded bg-accent/35' />
								<Skeleton className='h-3 w-2/3 rounded bg-accent/25' />
								<Skeleton className='h-2.5 w-1/2 rounded bg-accent/15' />
							</div>
						</div>
					))}
				</div>
			</div>
		</SkeletonRegion>
	)
}
